<template>
  <v-card outlined>
    <v-card-text class="pa-2">
      <v-container class="pa-0">
        <v-row no-gutters>
          <v-col cols="12" class="pa-0 mb-1">{{ label }}</v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="min"
              type="number"
              :label="$t('components.OAmountRangeFilter.min') | uppercase"
              outlined
              dense
              hide-details
              clearable
              @input="emitValue"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="max"
              type="number"
              :label="$t('components.OAmountRangeFilter.max') | uppercase"
              outlined
              dense
              hide-details
              clearable
              @input="emitValue"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="4">
            <v-select
              v-model="currency"
              :items="currencyItems"
              :label="$t('components.OAmountRangeFilter.currency')"
              :loading="loading"
              outlined
              dense
              hide-details
              @change="emitValue"
            ></v-select>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script lang="js">
  // Currency choice is intentionally limited to EUR for now (RTLOMS-542); the list
  // is centralized here so adding currencies later only touches this component.
  const DEFAULT_CURRENCY = 'EUR'
  const SUPPORTED_CURRENCIES = [DEFAULT_CURRENCY]

  /**
   * Amount-range filter input: a currency selector (limited to EUR) plus a min/max
   * money range, prefixed by the field label. Mirrors the ODateRangeFilter contract
   * (v-model object value + `label`/`loading` props) so it slots into FilterDialog.
   *
   * Emitted value matches the refunds list `filters` contract for amount fields:
   * `{ min, max, currencies: [...] }` where min/max are numeric major units, converted
   * server-side with each currency's minor unit.
   */
  export default {
    name: 'OAmountRangeFilter',
    props: {
      value: {
        type: Object,
        default: () => ({}),
      },
      label: {
        type: String,
        default: '',
      },
      loading: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        min: this.value?.min ?? '',
        max: this.value?.max ?? '',
        currency: this.value?.currencies?.[0] ?? DEFAULT_CURRENCY,
      }
    },
    computed: {
      currencyItems() {
        return SUPPORTED_CURRENCIES.map((code) => ({ text: code, value: code }))
      },
    },
    watch: {
      // Re-sync local state when the parent resets the bound value (e.g. dialog reset).
      value(newValue) {
        this.min = newValue?.min ?? ''
        this.max = newValue?.max ?? ''
        this.currency = newValue?.currencies?.[0] ?? DEFAULT_CURRENCY
      },
    },
    methods: {
      hasValue(bound) {
        return bound !== '' && bound !== null && bound !== undefined
      },
      // Emit an empty object when neither bound is set so FilterDialog's active-filter
      // detection does not flag the filter on the (always-present) currency alone.
      // Empty bounds are left undefined so JSON.stringify drops them from the payload.
      emitValue() {
        if (this.hasValue(this.min) || this.hasValue(this.max)) {
          this.$emit('input', {
            min: this.hasValue(this.min) ? Number(this.min) : undefined,
            max: this.hasValue(this.max) ? Number(this.max) : undefined,
            currencies: [this.currency],
          })
        } else {
          this.$emit('input', {})
        }
      },
    },
  }
</script>
